import { useCallback } from "react";

import { Container, Dialog, Flex, Portal } from "@chakra-ui/react";
import { createOverlay } from "@chakra-ui/react";
import {
  SecurityAlert,
  type SecurityRequirement,
  type SecuritySnapshot,
  type VerifyStatus,
} from "@features/two-fa/ui/security-alert";
import { type MessageDescriptor } from "react-intl";

import { SubPageTemplate } from "@shared/ui/templates/sub-page-template";

export interface SecurityOverlayProps {
  completedRequirements: SecurityRequirement[];
  description: MessageDescriptor;
  myRefetch?: VoidFunction;
  requirements: SecurityRequirement[];
  security: SecuritySnapshot;
  verificationStatus?: VerifyStatus;
  onBackExtra?: VoidFunction;
  onGoToKybVerify: () => Promise<void>;
  onGoToKycVerify: () => Promise<void>;
  onGoToOffer: () => Promise<void>;
  onGoToPhoneVerify: () => Promise<void>;
  onGoToPolicies: () => Promise<void>;
  onGoToQuestionnaire: () => Promise<void>;
  onGoToTwoFaVerify: () => Promise<void>;
}

export const securityOverlay = createOverlay<SecurityOverlayProps>((props) => {
  const {
    completedRequirements,
    description,
    myRefetch,
    requirements,
    security,
    verificationStatus,
    onBackExtra,
    onGoToKybVerify,
    onGoToKycVerify,
    onGoToOffer,
    onGoToPhoneVerify,
    onGoToPolicies,
    onGoToQuestionnaire,
    onGoToTwoFaVerify,
    onOpenChange,
    ...rest
  } = props;
  const handleBack = useCallback(() => {
    onBackExtra?.();
    onOpenChange?.({ open: false });
  }, [onOpenChange, onBackExtra]);

  const handleClick = useCallback(
    async (requirement: SecurityRequirement) => {
      switch (requirement) {
        case "policies":
          await onGoToPolicies();
          break;
        case "kyc-verify":
          await (security.policiesAccepted
            ? onGoToKycVerify()
            : onGoToPolicies());
          break;
        case "kyb-verify":
          await (security.policiesAccepted
            ? onGoToKybVerify()
            : onGoToPolicies());
          break;
        case "questionnaire":
          await onGoToQuestionnaire();
          break;
        case "two-fa":
          await onGoToTwoFaVerify();
          break;
        case "phone":
          await onGoToPhoneVerify();
          break;
        case "offer":
          await onGoToOffer();
          break;
      }
      onOpenChange?.({ open: false });
    },
    [
      security.policiesAccepted,
      onGoToKybVerify,
      onGoToKycVerify,
      onGoToOffer,
      onGoToPhoneVerify,
      onGoToPolicies,
      onGoToQuestionnaire,
      onGoToTwoFaVerify,
      onOpenChange,
    ],
  );

  return (
    <Dialog.Root {...rest} modal={false} scrollBehavior="inside" size="full">
      <Portal>
        <Dialog.Positioner top="16">
          <Dialog.Content
            backgroundColor="bg"
            boxShadow="none"
            overflowY="auto"
          >
            <Flex direction="column">
              <Container
                as={Flex}
                flex={1}
                flexDirection="column"
                maxW="lg"
                pb="24"
                pt="8"
              >
                <SubPageTemplate onBack={handleBack}>
                  <SecurityAlert
                    completedRequirements={completedRequirements}
                    description={description}
                    myRefetch={myRefetch}
                    requirements={requirements}
                    security={security}
                    verificationStatus={verificationStatus}
                    onClick={handleClick}
                    onCompleted={handleBack}
                  />
                </SubPageTemplate>
              </Container>
            </Flex>
          </Dialog.Content>
        </Dialog.Positioner>
      </Portal>
    </Dialog.Root>
  );
});

type SecurityOverlaySync = Pick<
  SecurityOverlayProps,
  "completedRequirements" | "requirements" | "security" | "verificationStatus"
>;

/**
 * `securityOverlay.update` в рантайме мержит пропсы, но типизирован как полный
 * `SecurityOverlayProps` — отсюда каст. Плюс он бросает, если оверлея с таким id
 * уже нет в сторе (закрыт и размонтирован): для фонового синка это не ошибка.
 */
export const updateSecurityOverlay = (
  id: string,
  props: SecurityOverlaySync,
) => {
  try {
    securityOverlay.update(id, props as SecurityOverlayProps);
  } catch {
    // оверлей закрыт — обновлять нечего
  }
};

import { closeAllOverlayInstances } from "@shared/ui/chakra/overlays/close-all-overlay-instances";

export const closeAllSecurityOverlays = () => {
  closeAllOverlayInstances(securityOverlay);
};
